import { shuffle, nextRandom } from '../rng/rng';
import { getInstanceDefinition, getDefinition } from './cardLookup';
import { XP_TOKEN_COLORS, drawXpTokenColor, returnXpTokenColor } from '../state/xpTokens';

// Caverns of Bane, Disowned Bloodrager (L3): "This hero can equip two
// weapons." Every other hero caps at 1 - `player.equippedWeapons` is a
// `heroInstanceId -> weaponInstanceId[]` map (an array even for the normal
// 1-weapon case) precisely so this one card doesn't need its own parallel
// data structure; `getWeaponCapacity` is the single place that decides how
// many a given hero is allowed to hold at once.
const MULTI_WEAPON_HERO_IDS = new Set(['hero-disowned-l3']);
export function getWeaponCapacity(heroDef) {
  return MULTI_WEAPON_HERO_IDS.has(heroDef.id) ? 2 : 1;
}

// Every weaponInstanceId currently equipped to this hero, in equip order
// (oldest first) - never undefined, so callers don't need `|| []` at every
// call site.
export function getEquippedWeaponIds(player, heroInstanceId) {
  return player.equippedWeapons[heroInstanceId] || [];
}

// Numenera, Punch Dagger: "Heroes may equip 2 of this card." A narrower,
// per-WEAPON-CARD version of "more than 1 equipped" than Disowned
// Bloodrager's own per-HERO exception above - ANY hero can hold 2, but only
// if BOTH are Punch Dagger specifically. A hero mixing 1 Punch Dagger with
// 1 different weapon is still capped at the hero's own normal capacity -
// the exception is scoped to "2 of THIS card," not a general capacity
// increase, so it only raises the effective cap when every ALREADY-equipped
// weapon on this hero is also a Punch Dagger. getWeaponCapacity alone can't
// express this (it only looks at the hero, not what's actually equipped or
// being equipped), hence this wrapper - dungeonActions.js's equipWeapon is
// the only real enforcement point that needs it (the only place a weapon is
// actually gained).
export function getWeaponCapacityFor(state, player, heroInstanceId, heroDef, weaponDef) {
  const baseCapacity = getWeaponCapacity(heroDef);
  if (weaponDef.id !== 'village-punch-dagger') return baseCapacity;
  const alreadyEquippedIds = getEquippedWeaponIds(player, heroInstanceId);
  const allPunchDaggers = alreadyEquippedIds.every((id) => getInstanceDefinition(state, id).id === 'village-punch-dagger');
  return allPunchDaggers ? Math.max(baseCapacity, 2) : baseCapacity;
}

// Milestone 1 simplification, still used for simple always-active numeric
// trait lines (e.g. "Physical Attack +2"). Anything that doesn't match one of
// these patterns contributes nothing here - it's handled by a bespoke
// handler in effectRegistry.js instead, or is simply not yet implemented.
const PATTERNS = [
  [/^Physical Attack \+(\d+)$/, (n, out) => { out.physicalAttack += n; }],
  // Root of Corruption, Maul: "Physical Attack -2" - a printed negative
  // weapon stat, same shape as the positive pattern above (not a dynamic
  // ability), so generalized here rather than special-cased per card.
  [/^Physical Attack -(\d+)$/, (n, out) => { out.physicalAttack -= n; }],
  [/^Magic Attack \+(\d+)$/, (n, out) => { out.magicAttack += n; }],
  [/^Magic Attack -(\d+)$/, (n, out) => { out.magicAttack -= n; }],
  [/^Attack -(\d+)$/, (n, out) => { out.attackReduction += n; }],
  [/^Light \+(\d+)$/, (n, out) => { out.light += n; }],
];

// Used by effectRegistryCoverage.test.js to find alwaysActive lines that
// parseAlwaysActiveTraits's regexes won't match - e.g. "Dwarf heroes of any
// Strength may equip this weapon" or "Additional Magic Attack equal to the
// level of the equipped hero" describe real mechanics, not simple flat
// bonuses, and silently contribute nothing unless specially handled
// elsewhere (see SPECIAL_CASE_ALWAYS_ACTIVE/ALWAYS_ACTIVE_GAPS in that test).
export function isSimpleNumericAlwaysActive(text) {
  return PATTERNS.some(([regex]) => regex.test(text.trim()));
}

// Matt asked for this: "instead of saying use ability it said draw one
// card if we could make that work for whatever abilities exist that would
// be pretty cool, maybe with a fallback." A pure text transform on the
// ability's own printed text, not a per-card lookup table - so it
// generalizes to every card (including future ones) instead of needing an
// entry maintained per card, matching this codebase's general preference
// for a small generic rule over a big one-off table (see
// MONSTER_IMMUNITIES, OVERRIDABLE_STATS). Strips a leading "If ...,"
// condition (redundant once the button is even showing/enabled - see
// isUsable, which already gates on that same condition) and keeps just the
// first sentence, since these texts consistently front-load the primary
// action and tack a secondary detail on afterward ("Draw 1 card. If it is a
// hero it gains Physical Attack +2." -> "Draw 1 card."). Returns null (not
// a guess) when there's truly nothing to summarize, so callers can fall
// back to a generic "Use ability" label themselves.
export function summarizeAbilityText(text) {
  if (!text) return null;
  const withoutCondition = text.trim().replace(/^If [^,]+,\s*/i, '');
  const firstSentence = withoutCondition.match(/^[^.]+\.?/)?.[0]?.trim();
  if (!firstSentence) return null;
  return firstSentence.charAt(0).toUpperCase() + firstSentence.slice(1);
}

export function parseAlwaysActiveTraits(abilityTextEntries) {
  const out = {
    physicalAttack: 0, magicAttack: 0, attackReduction: 0, light: 0,
  };
  for (const entry of abilityTextEntries || []) {
    const text = entry.text.trim();
    for (const [regex, apply] of PATTERNS) {
      const match = text.match(regex);
      if (match) {
        apply(Number(match[1]), out);
        break;
      }
    }
  }
  return out;
}

// Estimate a single hero's own Attack Value (its base trait line plus its
// equipped weapon's, if any) - distinct from attackMath.computeBaseAttack,
// which totals the whole party. Originally built just for Belac's "highest
// Attack Value" comparison (effectRegistry/bearers.js); Caverns of Bane's
// Rot Grub ("Choose 1 hero. If that hero's Attack Value is 5 or higher...")
// needs the exact same per-hero estimate, so it lives here as a shared
// primitive rather than duplicated.
export function estimateHeroAttackValue(state, player, heroInstanceId) {
  const heroDef = getInstanceDefinition(state, heroInstanceId);
  const heroTraits = parseAlwaysActiveTraits(heroDef.abilityText.alwaysActive);
  let total = heroTraits.physicalAttack + heroTraits.magicAttack;
  for (const weaponId of getEquippedWeaponIds(player, heroInstanceId)) {
    const weaponTraits = parseAlwaysActiveTraits(getInstanceDefinition(state, weaponId).abilityText.alwaysActive);
    total += weaponTraits.physicalAttack + weaponTraits.magicAttack;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Mutation primitives for hand-authored effect handlers (effectRegistry.js).
// Every function is pure: (state, ...) -> newState (or [newState, extra]),
// never mutating its input. Handlers compose these rather than reaching into
// GameState's shape directly, so zone-movement rules (e.g. "destroyed curses
// go to the bottom of the curse deck, not removed from the game") live in
// exactly one place.
// ---------------------------------------------------------------------------

function updatePlayer(state, playerId, updateFn) {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)),
  };
}

function removeFromArray(array, instanceId) {
  const i = array.indexOf(instanceId);
  if (i === -1) return array;
  return [...array.slice(0, i), ...array.slice(i + 1)];
}

// A card leaving the hand for any reason can no longer be a valid equipped
// weapon - without this, a hero whose equipped weapon got destroyed/discarded
// by an unrelated effect (Cinder Drake's "destroy 1 weapon", Ogre Mage's
// "discard 1 card with Magic Attack", etc.) kept a stale equippedWeapons
// entry pointing at a gone instance, which then permanently blocked
// EQUIP_WEAPON from ever giving that hero a replacement this turn ("already
// equipped with a weapon" - even though the original is long gone). Caught
// while fixing Ogrillon's "discard 1 equipped weapon", which used to do this
// cleanup itself as a one-off special case; generalized here since any
// hand-removal can hit an equipped weapon, not just Ogrillon's own effect.
function unequipIfEquippedWeapon(player, instanceId) {
  const heroInstanceId = Object.keys(player.equippedWeapons).find((h) => player.equippedWeapons[h].includes(instanceId));
  if (!heroInstanceId) return player.equippedWeapons;
  // Remove only THIS weapon from that hero's list - Disowned Bloodrager can
  // hold 2 at once, and losing one (e.g. to Ogre Mage's "discard 1 card with
  // Magic Attack") shouldn't also strip the other, still-equipped weapon.
  const remaining = player.equippedWeapons[heroInstanceId].filter((id) => id !== instanceId);
  const equippedWeapons = { ...player.equippedWeapons };
  if (remaining.length) equippedWeapons[heroInstanceId] = remaining;
  else delete equippedWeapons[heroInstanceId];
  return equippedWeapons;
}

// Root of Corruption, Rage of the Disowned: "specialEffects: This card
// cannot be discarded or destroyed." Unconditional (unlike the SAME card's
// own Dungeon-ability line, which protects a CHOSEN HERO and only against
// Dungeon abilities/Battle effects specifically - see effectRegistry/
// villages.js's turnFlags.protectedHeroInstanceIds and battleTargetableHeroes
// below) - so it's checked here, at the front of both single choke points,
// rather than scoped to any one triggering source. A silent no-op (not a
// thrown error) is correct: this must hold even against a MANDATORY effect
// that has no way to know to skip it (e.g. "each player destroys 1 card"),
// mirroring the rulebook's general "do what you can" resolution rule for a
// forced effect that can't legally apply to one of its targets. Deliberately
// does NOT protect against the routine end-of-turn hand-clear (turnActions.js's
// endTurn bypasses both these helpers entirely for that sweep already - see
// discardFromHand's own comment below) - "cannot be discarded or destroyed"
// reads as protection from other cards' effects, not permanent hand-lock.
// Blood Debt ("This card can only be destroyed by itself") is a narrower
// variant of the same idea - it only restricts DESTROY (the printed text
// says nothing about discard), and only its own Village ability is allowed
// to be the source, so a blanket allowlist can't express it alone; see
// SELF_DESTROY_ONLY and destroyFromHand's own selfDestroy parameter below.
const IMMUNE_TO_ALL_DISCARD_DESTROY = new Set(['village-rage-of-the-disowned']);

// Root of Corruption, Blood Debt: "specialEffects: This card can only be
// destroyed by itself." Every destroyFromHand call defaults selfDestroy to
// false and is silently blocked for a card in this set unless its OWN
// registered handler explicitly opts in - effectRegistry/villages.js's
// village-blood-debt is the only such call site today. A silent no-op here
// for the same "do what you can" reasoning as IMMUNE_TO_ALL_DISCARD_DESTROY
// above - discard is untouched (the printed text only restricts destroy).
const SELF_DESTROY_ONLY = new Set(['village-blood-debt']);

// Removed from the game entirely - not moved to any zone (rulebook: "destroy").
// The single choke point every "destroy 1 card" effect passes through, so
// stats.cardsDestroyed is incremented here rather than at each individual
// card handler - Rest is the one exception (see restActions.js, refactored
// to go through here too rather than inlining its own hand-filter).
//
// Curse cards are the one exception to "removed from the game entirely":
// per the curse-deck rule (see gainCurse/destroyCurseFromHand below), a
// destroyed curse always cycles back to the bottom of the curse deck
// instead. Matt, confirming this applies universally: "in every case in
// this game diseases and curses are interchangeable. They are two words
// for the same thing." Routing through THIS choke point (rather than
// requiring every caller to know to call destroyCurseFromHand instead)
// fixes several pre-existing card handlers that called plain destroyFromHand
// on a card they knew to be a disease and unintentionally removed it from
// the game forever instead of curing it back into the shared deck (e.g.
// Honormain, Profaned, Charming, Cathedral, Chalice, Regian - all now
// correct automatically, no per-card changes needed).
// Numenera, Intelligent/Intelligent Jack/Enhanced Intelligent Jack: "React:
// After a card is destroyed, add Physical Attack +2[/+4 and Light +2]."
// Matt, scoping the trigger: only destroys happening during the
// Intelligent-holder's OWN turn (self-pay costs, or a monster's Battle/
// Aftermath effect against their own hand) - not literally any destroy by
// any player anywhere, which the unqualified printed text would otherwise
// read as, and which would turn every destroyFromHand call in the engine
// (including OTHER players' routine self-pay costs) into a blocking prompt
// for this player. Optional per the same general React rule already
// confirmed for Shield Bearer/Smuggler - offered via
// queueIntelligentReactOffer rather than applied automatically. Only
// offers ONE eligible copy per destroy event (first found in hand order) -
// a player holding 2+ copies needs a separate destroy event per copy to
// react with each, same "once per turn per card instance" React rule.
export const INTELLIGENT_REACT_BONUS = {
  'hero-intelligent-l1': { physicalAttack: 2, light: 0 },
  'hero-intelligent-l2': { physicalAttack: 2, light: 0 },
  'hero-intelligent-l3': { physicalAttack: 4, light: 2 },
};
function offerIntelligentReact(state, ownerPlayerId) {
  const activePlayer = state.players[state.meta.activePlayerIndex];
  if (!activePlayer || activePlayer.id !== ownerPlayerId) return state;
  const eligibleId = activePlayer.hand.find((id) => {
    const def = getInstanceDefinition(state, id);
    return INTELLIGENT_REACT_BONUS[def.id] && !activePlayer.turnFlags.usedAbilityInstanceIds.includes(`${id}:react:0`);
  });
  if (!eligibleId) return state;
  return queueIntelligentReactOffer(state, { playerId: ownerPlayerId, instanceId: eligibleId });
}
export function queueIntelligentReactOffer(state, { playerId, instanceId }) {
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: { ...state.meta, pendingHandChoices: [...queue, { kind: 'intelligentReactOffer', playerId, instanceId }] },
  };
}

// Numenera, Enhanced Rugged Jack's React ("take a prepare action"): queues
// a full "which cards to keep, in what order" decision - richer than every
// other queued kind here (not a pick-1-of-several, nor a plain accept/
// decline), so it carries no candidates of its own; the actual hand is
// read live when actions/handChoiceActions.js's resolvePreparePick
// resolves it. See actions/prepareActions.js's applyBonusPrepare for why
// this can't just reuse the normal PREPARE action.
export function queuePreparePick(state, { playerId, reason }) {
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: { ...state.meta, pendingHandChoices: [...queue, { kind: 'preparePick', playerId, reason }] },
  };
}

// Worlds Collide, Ambrosia: "Whenever a Battle or Aftermath effect destroys
// a hero, discard that hero instead." (a Dungeon-ability GRANT for the turn,
// turnFlags.battleAftermathDestroyRedirectsToDiscard) / Guardian Blade:
// "specialEffects: If the equipped hero would be destroyed, discard that
// hero instead." (always-active while equipped). Single predicate for both
// unconditional redirects - handChoiceActions' destroyHeroOrOfferShieldBearer
// checks it on the queued-choice path, and destroyFromHand itself checks it
// (below) for Battle/Aftermath handlers that destroy algorithmically without
// queueing a choice.
export function heroDestroyRedirectsToDiscard(state, playerId, instanceId, def) {
  if (!isHeroLike(def)) return false;
  const player = state.players.find((p) => p.id === playerId);
  if (player.turnFlags.battleAftermathDestroyRedirectsToDiscard) return true;
  return getEquippedWeaponIds(player, instanceId).some((id) => getInstanceDefinition(state, id).id === 'village-guardian-blade');
}

export function destroyFromHand(state, playerId, instanceId, { selfDestroy = false, battleEffect = false } = {}) {
  const def = getInstanceDefinition(state, instanceId);
  if (IMMUNE_TO_ALL_DISCARD_DESTROY.has(def.id)) return state;
  if (SELF_DESTROY_ONLY.has(def.id) && !selfDestroy) return state;
  // Matt: "I don't think the ambrosia protected my regular like it should
  // have." It only protected destroys routed through the pendingHandChoices
  // queue - Battle/Aftermath handlers that pick their own target (Snakewyrm's
  // "Destroy 2 Regulars", Smokeplume Drake's highest-Strength hero, ...) came
  // straight here and bypassed the redirect. Those call sites now declare
  // themselves with battleEffect: true; it is NOT the default, because
  // self-pay destroy costs and Village-ability destroys must never redirect.
  if (battleEffect && heroDestroyRedirectsToDiscard(state, playerId, instanceId, def)) {
    return discardFromHand(state, playerId, instanceId);
  }
  if (def.traits?.includes('Curse')) {
    const nextState = updatePlayer(destroyCurseFromHand(state, playerId, instanceId), playerId, (p) => ({
      ...p, stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
    }));
    return offerIntelligentReact(nextState, playerId);
  }
  // Rulebook (TS7_Rules_Web.pdf, clarifications): "Basic cards (Longspear,
  // Regular, and Torch) are placed back on their village stacks if
  // destroyed from a player's hand. If basic cards are destroyed from a
  // village stack, they are removed from the game." The second, narrower
  // exception to this function's removed-from-the-game default, alongside
  // Curse's cycle back to the curse deck above - and what keeps Royal
  // Summons renewable (Matt relayed Brian's report of its Spoils
  // permanently draining the 6-card Regular stack). Only a destroy FROM
  // HAND - no stack-destroy effect routes through here. Guarded on the
  // stack existing: in randomized-village mode a basic stack is only built
  // when something in play needs it (villageSetup's
  // REGULAR_STACK_DEPENDENCIES), and with no stack to return to,
  // removed-from-the-game is the only place left. Returned to the FRONT:
  // buys take stack[0] (villageActions' getStackArray callers), so front
  // is the top of the stack.
  if (def.type === 'basic' && state.village.basicStacks?.[def.id]) {
    const withReturn = {
      ...state,
      village: {
        ...state.village,
        basicStacks: { ...state.village.basicStacks, [def.id]: [instanceId, ...state.village.basicStacks[def.id]] },
      },
    };
    const nextState = updatePlayer(withReturn, playerId, (p) => ({
      ...p,
      hand: removeFromArray(p.hand, instanceId),
      equippedWeapons: unequipIfEquippedWeapon(p, instanceId),
      stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
    }));
    return offerIntelligentReact(nextState, playerId);
  }
  const nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    hand: removeFromArray(p.hand, instanceId),
    equippedWeapons: unequipIfEquippedWeapon(p, instanceId),
    stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
  }));
  return offerIntelligentReact(nextState, playerId);
}

// Caverns of Bane, Xobmokt Ichor King: "Whenever a player discards a card
// before the end of the turn, destroy that card." Checked from
// discardFromHand below (the single choke point every "discard 1 card"
// EFFECT passes through) rather than at each individual discard call-site -
// broader than any other hall-presence effect in this engine (those all
// read state once at a fixed point; this applies to every future discard
// for the rest of the turn while Xobmokt sits in the hall). The routine
// end-of-turn hand-clear deliberately bypasses discardFromHand entirely
// (see its own comment below), which already matches Xobmokt's own "before
// the end of the turn" wording with no extra guard needed here.
function isXobmoktInHall(state) {
  return state.dungeonHall.some((id) => id && getInstanceDefinition(state, id).id === 'bearer-xobmokt-ichor-king');
}

// Same choke-point reasoning as destroyFromHand - stats.cardsDiscardedByEffect
// counts cards an EFFECT made you discard (curses, Battle/Aftermath, forced
// hand choices), not the routine "discard the rest of your hand" at end of
// turn (turnActions.js's endTurn builds that discard list inline, never
// through this helper, so it's naturally excluded).
export function discardFromHand(state, playerId, instanceId) {
  if (IMMUNE_TO_ALL_DISCARD_DESTROY.has(getInstanceDefinition(state, instanceId).id)) return state;
  let nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    hand: removeFromArray(p.hand, instanceId),
    discard: [...p.discard, instanceId],
    equippedWeapons: unequipIfEquippedWeapon(p, instanceId),
    stats: { ...p.stats, cardsDiscardedByEffect: p.stats.cardsDiscardedByEffect + 1 },
  }));
  // Xobmokt redirects THIS discard into a destroy - a curse still cycles
  // back to the curse deck rather than vanishing (same universal rule as
  // destroyFromHand above: Xobmokt's own text says "destroy," not some
  // special "remove from the game" wording).
  if (isXobmoktInHall(nextState)) {
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: removeFromArray(p.discard, instanceId) }));
    const def = getInstanceDefinition(nextState, instanceId);
    if (def.traits?.includes('Curse')) {
      nextState = { ...nextState, village: { ...nextState.village, curseDeck: [...nextState.village.curseDeck, instanceId] } };
    }
  }
  return nextState;
}

// Draws up to `count` cards, reshuffling discard into deck if the deck runs
// dry mid-draw. Returns the new state directly (consumes/updates
// state.meta.rngState internally, same reshuffle rule as end-of-turn draws).
export function drawCards(state, playerId, count) {
  const player = state.players.find((p) => p.id === playerId);
  let deck = [...player.deck];
  let discard = [...player.discard];
  let rngState = state.meta.rngState;
  const drawn = [];
  while (drawn.length < count) {
    if (deck.length === 0) {
      if (discard.length === 0) break;
      const [shuffled, next] = shuffle(discard, rngState);
      rngState = next;
      deck = shuffled;
      discard = [];
    }
    drawn.push(deck.shift());
  }
  const nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    deck,
    discard,
    // Brian: "When I drew my familiar in the dungeon with a dungeon ability,
    // it got discarded with my hand instead of being set aside as it should
    // be."
    //
    // Rulebook, "Familiars": "Once taken, a familiar is yours for the rest of
    // the game. Place the familiar in front of you, face up. It remains in
    // play until you use one of its abilities" - so the ONLY thing that ever
    // takes a familiar out of play is using one of its abilities. The printed
    // "At the start of your turn, if you have your familiar in your hand,
    // place it face up in front of you once more" line covers the ordinary
    // case (it cycles back in the fresh end-of-turn hand), and
    // turnActions.js's endTurn already implements exactly that.
    //
    // Nothing covered a MID-turn draw, though: the familiar just sat in hand
    // as an ordinary card, and endTurn's hand-clear then swept it into the
    // discard pile - taking it out of play without an ability ever being
    // used, and costing the player their familiar for however many turns it
    // took to cycle back around. Handled here, in the one helper every
    // mid-turn effect draw goes through, so it lands in the familiar zone the
    // moment it's drawn. No replacement card, matching the start-of-turn
    // rule's own "Do not draw an additional card to replace it."
    ...(p.familiarInstanceId && drawn.includes(p.familiarInstanceId) && !p.inPlayInstanceIds.includes(p.familiarInstanceId)
      ? {
        hand: [...p.hand, ...drawn.filter((id) => id !== p.familiarInstanceId)],
        inPlayInstanceIds: [...p.inPlayInstanceIds, p.familiarInstanceId],
      }
      : { hand: [...p.hand, ...drawn] }),
  }));
  return { ...nextState, meta: { ...nextState.meta, rngState } };
}

// Uniformly picks one instanceId at random from `candidateInstanceIds` (a
// caller-supplied list - typically a player's own hand, but not assumed to
// be), using the same seeded rng/rng.js stream as drawCards/shuffle above
// (never Math.random() - eslint-enforced). Returns { state, instanceId }
// (instanceId is null, state unchanged, if the list is empty). Into the
// Abyss, Verdan Minstrel: "Each other player with 6 or more cards discards 1
// random card" is the first card in this project needing "pick 1 random
// card from a player's HAND" - a generic primitive rather than a one-off,
// since Root of Corruption's Gnoll (random reveal from DISCARD) and
// Numenera's Unstable Crystals (random destroy from HAND) both print the
// same "random card" shape and were previously left unimplemented for
// exactly this missing primitive (see docs/expansion-audit.md) - both still
// need their own additional mechanics (discard-pile reveal-and-choose;
// self-referential copy-count condition) so aren't retrofitted here, but a
// future pass on either can now reuse this directly.
export function pickRandomInstanceId(state, candidateInstanceIds) {
  if (!candidateInstanceIds.length) return { state, instanceId: null };
  const [rand, rngState] = nextRandom(state.meta.rngState);
  const index = Math.floor(rand * candidateInstanceIds.length);
  return { state: { ...state, meta: { ...state.meta, rngState } }, instanceId: candidateInstanceIds[index] };
}

// Root of Corruption, Gnoll (Thieving Cur/Scavenging Cur): "Randomly select
// and reveal 3 cards from your discard pile." The first card needing
// MULTIPLE random picks at once, without replacement - repeatedly calls
// pickRandomInstanceId above, shrinking the pool each time, rather than a
// separate random-sampling algorithm. Fewer than `count` if the pool itself
// is smaller ("do what you can", same convention as applyDoWhatYouCan).
// Returns { state, instanceIds } (instanceIds in the order they were
// drawn - not meaningful here, just deterministic given the seed).
export function pickRandomInstanceIds(state, candidateInstanceIds, count) {
  let pool = [...candidateInstanceIds];
  let nextState = state;
  const picked = [];
  while (picked.length < count && pool.length) {
    const { state: afterPick, instanceId } = pickRandomInstanceId(nextState, pool);
    nextState = afterPick;
    picked.push(instanceId);
    pool = pool.filter((id) => id !== instanceId);
  }
  return { state: nextState, instanceIds: picked };
}

// "Revealing a card from your deck": take the top card, apply the directed
// effect (the caller's job), then discard it - never added to hand (rulebook
// FAQ, "Revealing a card from your deck"). Returns { state, instanceId }
// (instanceId is null if the player has no cards anywhere left).
export function revealTopOfOwnDeck(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  let deck = [...player.deck];
  let discard = [...player.discard];
  let rngState = state.meta.rngState;
  if (deck.length === 0) {
    if (discard.length === 0) return { state, instanceId: null };
    const [shuffled, next] = shuffle(discard, rngState);
    rngState = next;
    deck = shuffled;
    discard = [];
  }
  const [instanceId, ...rest] = deck;
  const nextState = updatePlayer({ ...state, meta: { ...state.meta, rngState } }, playerId, (p) => ({
    ...p, deck: rest, discard: [...discard, instanceId],
  }));
  return { state: nextState, instanceId };
}

// "Look at the top N cards of your deck": distinct from revealTopOfOwnDeck
// above, whose revealed card is always committed to discard by default (the
// rulebook's "Revealing a card" default). A "Look at" effect's cards return
// to the deck by default instead - Caverns of Bane's Patternmage ("Look at
// the top N cards of your deck. Discard M of them. Place the rest on top of
// your deck [in any order].") is the first card needing this shape, so this
// pulls the top `count` cards off without committing them anywhere - the
// caller decides each card's fate (discard vs. back on top, in what order).
// Reshuffles discard into deck mid-look if the deck runs out, same as
// drawCards/revealTopOfOwnDeck, so a shorter deck+discard combo still
// returns as many cards as actually exist (fewer than `count`, never an
// error). Returns { state, instanceIds } in top-to-bottom order.
export function lookAtTopOfDeck(state, playerId, count) {
  let nextState = state;
  const instanceIds = [];
  for (let i = 0; i < count; i += 1) {
    const player = nextState.players.find((p) => p.id === playerId);
    let { deck, discard } = player;
    if (deck.length === 0) {
      if (discard.length === 0) break;
      const [shuffled, rngState] = shuffle(discard, nextState.meta.rngState);
      deck = shuffled;
      discard = [];
      nextState = { ...nextState, meta: { ...nextState.meta, rngState } };
    }
    const [instanceId, ...rest] = deck;
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, deck: rest, discard }));
    instanceIds.push(instanceId);
  }
  return { state: nextState, instanceIds };
}

export function grantGold(state, playerId, amount) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, bonusGold: p.turnFlags.bonusGold + amount },
  }));
}

// Numenera's colored-XP rule (state/xpTokens.js): XP is 6 colors of token
// drawn from a shared 54-token supply, not a plain number - but every other
// card's ability text (across every set) still just says "gain/discard N
// XP," and dozens of existing call sites (including a large chunk of the
// test suite) read/write `player.xp` as a plain number directly, predating
// this mechanic. So `xp` stays the AUTHORITATIVE total, updated exactly as
// before (floors at 0 on discard); `xpTokens` is a best-effort colored
// breakdown layered on top, updated whenever there's a real token to
// move (best-effort on draw: skip if the shared supply is exhausted;
// best-effort on discard: skip if this player's own tracked xpTokens don't
// have a matching color to return - e.g. because their `xp` total was set
// directly in a test/scenario setup without also granting matching
// tokens). A best-effort desync only ever means "fewer colored tokens
// visibly available to spend than the raw xp total," never a blocked or
// incorrect `xp` change - xpTokens is purely additive tracking for the new
// SPEND_XP_TOKEN action (actions/xpTokenActions.js), not a gate on it.
// xpTokenColors: see discardXpTokens's own comment - only meaningful when
// amount is negative (a discard); ignored on a gain.
export function grantXp(state, playerId, amount, { xpTokenColors } = {}) {
  if (amount > 0) return drawXpTokens(state, playerId, amount);
  if (amount < 0) return discardXpTokens(state, playerId, -amount, xpTokenColors);
  return state;
}

// Into the Abyss, Heart of Doom (Thunderstone Bearer): "Aftermath: If this
// card is not defeated, the active player loses." True elimination (Matt's
// own ruling) - flags the player rather than removing them from
// state.players (every array-length-assuming piece of code stays correct
// for free), so actions/turnActions.js's endTurn skips them when picking
// the next active player and scoring/scoring.js's computeFinalScores
// excludes them from winner contention. If this leaves exactly one
// non-eliminated player, the game ends immediately and that player wins
// outright, rather than waiting for the normal endTurn/scoring flow to
// notice - "the game would just end," per Matt, not a deferred check.
export function eliminatePlayer(state, playerId) {
  const players = state.players.map((p) => (p.id === playerId ? { ...p, eliminated: true } : p));
  const remaining = players.filter((p) => !p.eliminated);
  if (remaining.length <= 1) {
    return {
      ...state,
      players,
      meta: {
        ...state.meta, gameOver: true, endTrigger: 'eliminated', winnerId: remaining[0]?.id ?? null,
      },
    };
  }
  return { ...state, players };
}

function drawXpTokens(state, playerId, count) {
  let nextState = state;
  for (let i = 0; i < count; i += 1) {
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, xp: p.xp + 1 }));
    const [color, afterDraw] = drawXpTokenColor(nextState);
    if (!color) continue; // shared supply of 54 fully exhausted - xp itself still grants fine
    nextState = updatePlayer(afterDraw, playerId, (p) => ({ ...p, xpTokens: { ...p.xpTokens, [color]: p.xpTokens[color] + 1 } }));
  }
  return nextState;
}

// Bug report -Oxqycc8OLie0qvxFSJ2 (Matt): "Because we have different powers
// based on the different XP colors now when we level up a hero, the user
// ought to be able to select which XP pieces they want to discard." Before
// this, every "discard N XP" effect (leveling a hero chief among them) always
// auto-picked colors in XP_TOKEN_COLORS's own fixed order (blue first, then
// gray, etc.) - chosenColors lets a caller that already resolved a real
// player choice (village/leveling.js's performLevelUp, threaded from
// villageActions.levelUpHero's choices.xpTokenColors - the same "choices
// resolved before dispatch" convention as that same function's own
// useSeedbombInstanceId, since which colors are available is fully knowable
// before dispatch, unlike a post-hoc pending-choice case like Caldera
// Dragon's) name exactly which of the count tokens being discarded come from
// which color, in order. Any of the count NOT covered by chosenColors (an
// empty/undefined array for every caller that hasn't been updated yet, or a
// genuine shortfall - e.g. a test/scenario built player.xp directly without
// matching xpTokens, see this file's own big xpTokens comment above) still
// falls back to the old auto-pick, so nothing not explicitly opting in to a
// choice changes behavior.
function discardXpTokens(state, playerId, count, chosenColors = []) {
  if (chosenColors.length > count) {
    throw new Error(`Chose ${chosenColors.length} XP tokens to discard, but only ${count} are being discarded.`);
  }
  const remainingChoices = [...chosenColors];
  let nextState = state;
  for (let i = 0; i < count; i += 1) {
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, xp: Math.max(0, p.xp - 1) }));
    const player = nextState.players.find((p) => p.id === playerId);
    const chosenColor = remainingChoices.shift();
    let color;
    if (chosenColor) {
      if (!player.xpTokens[chosenColor]) throw new Error(`You have no ${chosenColor} XP tokens to discard.`);
      color = chosenColor;
    } else {
      color = XP_TOKEN_COLORS.find((c) => player.xpTokens[c] > 0);
    }
    if (!color) continue; // no tracked colored tokens to return - xp itself still floors at 0 fine
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, xpTokens: { ...p.xpTokens, [color]: p.xpTokens[color] - 1 } }));
    nextState = returnXpTokenColor(nextState, color);
  }
  return nextState;
}

export function grantPhysicalAttack(state, playerId, amount) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, bonusPhysicalAttack: p.turnFlags.bonusPhysicalAttack + amount },
  }));
}

export function grantMagicAttack(state, playerId, amount) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, bonusMagicAttack: p.turnFlags.bonusMagicAttack + amount },
  }));
}

export function grantLight(state, playerId, amount) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, bonusLight: p.turnFlags.bonusLight + amount },
  }));
}

export function grantBuys(state, playerId, amount) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, bonusBuys: p.turnFlags.bonusBuys + amount },
  }));
}

// Curse deck: index 0 = top (drawn from here); destroyed curses return to the
// bottom (end of array), face down, never removed from the game (rulebook,
// "Curse Cards"). No-op if the curse deck is empty ("that player gets lucky").
export function gainCurse(state, playerId) {
  if (!state.village.curseDeck.length) return state;
  const [instanceId, ...rest] = state.village.curseDeck;
  const nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    discard: [...p.discard, instanceId],
    stats: { ...p.stats, cursesGained: p.stats.cursesGained + 1 },
    // Worlds Collide, Creeping Death: "Each other player who has NOT
    // gained a disease this turn gains 1 disease." Set unconditionally
    // here (the single choke point every "gain 1 disease" effect already
    // passes through), not scoped to Creeping Death specifically.
    turnFlags: { ...p.turnFlags, gainedDiseaseThisTurn: true },
  }));
  return { ...nextState, village: { ...nextState.village, curseDeck: rest } };
}

export function destroyCurseFromHand(state, playerId, instanceId) {
  const withoutCard = updatePlayer(state, playerId, (p) => ({ ...p, hand: removeFromArray(p.hand, instanceId) }));
  return { ...withoutCard, village: { ...withoutCard.village, curseDeck: [...withoutCard.village.curseDeck, instanceId] } };
}

// A card with an explicit goldValue of 0 IS a valid target for "gold value X
// or higher" effects; a card with no goldValue field at all (curses) is not
// (rulebook, "Zero and No Value"). Returns undefined, never 0, for the latter.
export function goldValueOf(def) {
  return def.goldValue;
}

export function hasTraitPresent(state, playerId, trait) {
  return state.players.find((p) => p.id === playerId).hand
    .some((instanceId) => getInstanceDefinition(state, instanceId).traits.includes(trait));
}

// "Hero-like" rather than def.type === 'hero': the Regular basic card (type
// 'basic', per the rulebook's card-type taxonomy) still has a Strength stat
// and counts as a hero for every card-text purpose - rulebook, "Level Up":
// "As noted in their keyword line, Regulars are hero cards." A card effect
// that says "destroy/discard/borrow a hero" (or counts heroes in hand) with
// no group/stack mechanics involved must use this, not a raw type check, or
// it silently skips every Regular in a player's hand - see monsters.js's
// Ogre/Ettin/Ogre Mage/Soulstolen/Decayed Heartwood/Smokeplume Drake
// Aftermath handlers, first caught when Matt reported an Aftermath "destroy
// a hero" effect not triggering despite winning the fight (his hand was
// mostly Regulars). Village/level-stack mechanics that key off def.group
// (Whetmage, Veteran Trainer, Necrophidius's Trophy) are a separate,
// deliberately-untouched case - Regular has no group of its own to level
// within, so those still correctly exclude it (see village/leveling.js's
// getRegularLevelUpCandidates for the different mechanic Regular actually
// uses to level up).
export function isHeroLike(def) {
  return def.strength !== undefined && def.strength !== null;
}

// Worlds Collide, The Last Doomknight (Thunderstone Bearer): "Global:
// Suffer Light -1. Each hero suffers Strength -1. Other monsters add
// Health +1." A whole-hall-state passive - checked for presence anywhere
// in the dungeon hall, not "when fighting this monster specifically" -
// same shape as Steam Torment/Plaguebearer's own Globals
// (combat/resolveCombat.js, which applies all 3 sub-effects).
export function anyLastDoomknightInHall(state) {
  return state.dungeonHall.some((id) => id && getInstanceDefinition(state, id).id === 'bearer-the-last-doomknight');
}

// The "Each hero suffers Strength -1" half of the Global above, factored
// out so it can be applied identically in two places: resolveCombat.js's
// own attack computation (the real thing that determines win/loss), and
// DungeonHall.vue's "Your Attack" preview (attackAgainst) - shown for
// every hall monster BEFORE the player commits to a fight. Those two used
// to compute Strength independently; the preview never knew about this
// Global at all, so it always overstated Total Attack Value by 1 per hero
// whenever a Doomknight sat anywhere in the hall. Matt reported this
// directly, twice: "It said I had 15 attack but then suddenly I had 12
// attack and I'm not sure what he did to affect me. He doesn't have any
// battle effects" (true - it's a Global, not a Battle effect, and the
// preview simply never applied it). No-op (returns state unchanged) if no
// Doomknight is present, so every other caller of computeBaseAttack stays
// exactly as before.
export function applyLastDoomknightHeroStrengthPenalty(state, playerId) {
  if (!anyLastDoomknightInHall(state)) return state;
  let nextState = state;
  for (const heroId of nextState.players.find((p) => p.id === playerId).hand) {
    const heroDef = getInstanceDefinition(nextState, heroId);
    if (!isHeroLike(heroDef)) continue;
    nextState = applyInstanceOverride(nextState, heroId, { strength: Math.max(0, heroDef.strength - 1) });
  }
  return nextState;
}

// Numenera, Murden/Blood Barm/Lord Mortis, and the Darkness monster:
// "Suffer Light -1" (specialEffects, alwaysActive - not a Battle-timed
// handler). Penumbra Fiend/Philethis encode the identical concept as a
// plain negative `monster.light` field instead of ability text - neither
// is consumed by computeTotalLight (which only scans the player's own
// hand), so both are handled generically here: any negative monster.light
// is a Light penalty, plus an explicit lookup for the text-based cards.
// The Last Doomknight's own "Suffer Light -1" folds in here too (its
// Strength -1/Health +1 halves are applyLastDoomknightHeroStrengthPenalty
// above and resolveCombat.js's own monster-Health bump respectively).
// Factored out (was private to resolveCombat.js) for the same reason as
// that function - DungeonHall.vue's "Your Attack" preview needs the exact
// same number resolveCombat.js will actually use, not an approximation
// that quietly omits every monster on this list.
const SUFFER_LIGHT_PENALTY = {
  'monster-murden': 1, 'monster-blood-barm': 1, 'monster-lord-mortis': 1, 'monster-darkness': 1,
  // Worlds Collide, Knightmare: "Suffer Light -2." - missing entirely from
  // source data until the WC numeric-stat audit (2026-07-22) found it on the
  // card scan; same shape as this table's other single-monster entries,
  // just a -2 instead of -1.
  'monster-knightmare': 2,
};
export function computeMonsterLightPenalty(state, monsterDef) {
  return Math.max(0, -(monsterDef.light || 0)) + (SUFFER_LIGHT_PENALTY[monsterDef.id] || 0)
    + (anyLastDoomknightInHall(state) ? 1 : 0);
}

// Worlds Collide, Gray Ooze/Isri/Clan: "Buy 1 [class]" (food/weapon), a
// class-restricted free buy granted by a card ability rather than the
// normal player-driven BUY_CARD action. No "restricted to trait X" choice
// UI exists (every other bonus-buy grant - Filigree Amulet, Icon of
// Inversion - is a fully generic extra buy the player spends via the
// normal action) - auto-picks the CHEAPEST matching, non-empty village-row
// stack instead (a reasonable "least wasteful" default, matching this
// engine's "Do What You Can" convention for other one-off auto-picked
// costs), rather than offering a real stack-choice UI for these 3 cards.
export function findCheapestBuyableSource(state, matches) {
  const candidates = findAllBuyableSources(state, matches);
  if (!candidates.length) return null;
  return { type: candidates[0].type, key: candidates[0].key };
}

// Same lookup as findCheapestBuyableSource, but returns every eligible
// non-empty stack (cheapest first) instead of collapsing straight to one.
// Trader (village-trader below) uses this to offer a real choice among
// options instead of always taking the cheapest - see that card's own
// comment for why it's the one exception to this file's usual "auto-pick
// cheapest, no choice UI" convention for these grant-a-card-of-type-X
// effects (Gray Ooze/Isri/Clan/Zafira's Weapon Ward/etc. still use the
// single-source function above unchanged).
export function findAllBuyableSources(state, matches) {
  const candidates = [];
  for (const key of Object.keys(state.village.villageStacks)) {
    if (state.village.villageStacks[key].length && matches(getDefinition(key))) {
      candidates.push({ type: 'village', key, cost: getDefinition(key).cost || 0 });
    }
  }
  for (const key of Object.keys(state.village.basicStacks)) {
    if (state.village.basicStacks[key].length && matches(getDefinition(key))) {
      candidates.push({ type: 'basic', key, cost: getDefinition(key).cost || 0 });
    }
  }
  candidates.sort((a, b) => a.cost - b.cost);
  return candidates;
}

// Pike (village-pike): "This hero is not affected by Battle effects." Only
// Battle - Aftermath/Raid/Global effects still apply normally - so every
// Battle-timing handler that targets a specific hero (across monsters.js AND
// bearers.js) needs to check this individually, rather than gating it once
// globally in resolveCombat.js.
//
// Numenera, Enhanced Graceful Glaive: "Immune to Battle and Aftermath
// effects." Only the Battle half is covered here (reusing every existing
// battleTargetableHeroes caller for free) - retrofitting every individual
// Aftermath handler across monsters.js/bearers.js with its own check (there
// is no single shared Aftermath-targeting helper the way Battle has this
// one) is out of scope for this pass; see docs/expansion-audit.md.
export function isShieldedByPike(state, player, heroInstanceId) {
  const isPike = getEquippedWeaponIds(player, heroInstanceId)
    .some((id) => getInstanceDefinition(state, id).id === 'village-pike');
  const isGracefulL3 = getInstanceDefinition(state, heroInstanceId).id === 'hero-graceful-l3';
  return Boolean(isPike || isGracefulL3);
}

// Root of Corruption, Rage of the Disowned: "One hero... cannot be
// discarded or destroyed by Dungeon abilities or Battle effects." The
// Battle half is enforced here, right alongside Pike's own shield, since
// this IS the shared "which heroes can a Battle effect legally
// discard/destroy" choke point every such handler already calls through
// (effectRegistry/villages.js's own Dungeon-ability handler populates
// turnFlags.protectedHeroInstanceIds; its Aftermath reads/clears the same
// list to destroy the hero itself once the protection's own scope has
// passed). The "Dungeon abilities" half is NOT enforced - no currently
// implemented Dungeon-timing ability generically discards/destroys an
// arbitrary hero chosen from hand (the few that touch a hero at all target
// their own card's own chosen hero as a self-paid cost, e.g. Greatsword),
// so there's no live call site that would need this check; see
// docs/expansion-audit.md.
export function battleTargetableHeroes(state, player, extraFilter = () => true) {
  return player.hand.filter((id) => {
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) && !isShieldedByPike(state, player, id)
      && !player.turnFlags.protectedHeroInstanceIds?.includes(id) && extraFilter(def);
  });
}

// Same Pike/Rage of the Disowned Battle-timed shield as battleTargetableHeroes
// above, but for handlers whose candidate list is NOT hero-only (e.g.
// "destroy 1 Fighter hero or Weapon card" - the Weapon half has no hero to
// shield). Strips shielded heroes out of an already-computed candidate list
// instead of building the list from scratch, so it composes with whatever
// non-hero matching a handler already did.
export function excludeShieldedHeroes(state, player, candidateInstanceIds) {
  return candidateInstanceIds.filter((id) => {
    const def = getInstanceDefinition(state, id);
    if (!isHeroLike(def)) return true;
    return !isShieldedByPike(state, player, id) && !player.turnFlags.protectedHeroInstanceIds?.includes(id);
  });
}

// Numenera, Shield Bearer: "React: When one or more heroes would be
// destroyed by a monster's effect, destroy this card instead." Unlike
// Pike/Rage of the Disowned above (a blanket immunity that removes a hero
// from the candidate pool before a choice is even offered), this is a real
// React ability - the rulebook's general "Abilities" rule ("you choose
// whether or not to use them") applies to every React, even ones without an
// explicit "may" on the card face (confirmed against the rulebook's own
// Smuggler example and glossary entry). So it can't gate candidates up
// front; it has to offer a substitution at the moment a SPECIFIC hero is
// actually about to be destroyed (see queueShieldBearerOffer/
// actions/handChoiceActions.js's resolveHandChoice interception). "A React
// ability can only be used once per turn" - tracked the same way as any
// other once-per-turn ability use (turnFlags.usedAbilityInstanceIds), keyed
// `${instanceId}:react:0` so it doesn't collide with this same card's own
// Dungeon ability use.
// Worlds Collide, Border Guard: "React: Destroy this card to prevent 1 hero
// from being destroyed by a Battle or Aftermath effect." Functionally
// identical to Shield Bearer's own React (same substitute-this-card-for-a-
// hero shape) - reused here rather than duplicated, even though Border
// Guard's own text is narrower (Battle/Aftermath only, not Raid) and this
// choke point doesn't distinguish which timing triggered the destroy - a
// documented narrow over-reach for the Raid case, see docs/expansion-audit.md.
const SHIELD_BEARER_LIKE_IDS = new Set(['village-shield-bearer', 'village-border-guard']);
export function findEligibleShieldBearer(state, player) {
  return player.hand.find((id) => {
    const def = getInstanceDefinition(state, id);
    return SHIELD_BEARER_LIKE_IDS.has(def.id) && !player.turnFlags.usedAbilityInstanceIds.includes(`${id}:react:0`);
  });
}

export function queueShieldBearerOffer(state, {
  playerId, heroInstanceId, shieldBearerInstanceId, reason,
}) {
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [{
        kind: 'shieldBearerOffer', playerId, heroInstanceId, shieldBearerInstanceId, reason,
      }, ...queue],
    },
  };
}

// Queues a mandatory "destroy/discard 1 of these cards" decision instead of
// silently auto-picking one - Matt: "if a monster's battle or aftermath
// requires you to destroy a card or hero, I should be able to choose."
// Can't resolve this up front like a Village/Dungeon ability's choice does
// (the "choices supplied before dispatch" convention) - Battle/Aftermath
// fire mid-dispatch inside CHOOSE_MONSTER, and which monster even needs a
// choice isn't known until it's actually fought. Uses the same pending-
// state + mandatory follow-up dispatch pattern as Caldera Dragon/Raid (see
// actions/handChoiceActions.js's resolveHandChoice), but as a FIFO queue: a
// single trigger can need more than one entry at once - "discard 2 cards"
// queues 2 single-pick entries, "each player destroys 1 card" queues one
// entry per player. Entries are NOT always owed to the active player -
// effects like Hellhound target other players, who must be able to resolve
// their own entry (see PlayerDock.vue's viewingPlayer/gameState watcher).
export function queueHandChoice(state, {
  playerId, verb, candidateInstanceIds, reason,
}) {
  if (!candidateInstanceIds.length) return state;
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: { ...state.meta, pendingHandChoices: [...queue, { playerId, verb, candidateInstanceIds, reason }] },
  };
}

// A different shape from queueHandChoice above: "you may choose to discard N
// cards; if you choose not to, you lose X XP" (Blazing Grimalkin) is an
// opt-in/decline gate, not a pick-1-of-several-candidates choice - there's
// nothing to highlight in hand until AFTER the player accepts. Resolved via
// actions/handChoiceActions.js's resolveOptionalDiscard, which - if
// accepted - queues `count` ordinary queueHandChoice discard entries off
// whatever the hand looks like at accept-time (not now, which could be
// stale by the time this player's turn in a shared FIFO queue comes up).
export function queueOptionalDiscard(state, {
  playerId, count, declineXpPenalty, reason,
}) {
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [...queue, {
        kind: 'optionalDiscard', playerId, count, declineXpPenalty, reason,
      }],
    },
  };
}

// Caverns of Bane, Curse of War: "Select 2 random cards from your hand
// (excluding this card). You may destroy 1 of them to destroy this card."
// A fifth shape, distinct from every one above: the candidates are fixed
// (already randomly chosen by the caller via pickRandomInstanceIds, unlike
// queueOptionalDiscard's own accept-time-sampled count) AND the choice is
// opt-in/decline (unlike queueHandChoice's mandatory pick from a fixed
// list) AND destroying the CHOSEN candidate also destroys a second,
// specific card (this curse itself) as a side effect. Declining is a pure
// no-op - the 2 revealed candidates just stay in hand, same as if nothing
// had happened.
export function queueOptionalCurseSelfDestroy(state, {
  playerId, selfInstanceId, candidateInstanceIds, reason,
}) {
  if (!candidateInstanceIds.length) return state;
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [...queue, {
        kind: 'optionalCurseSelfDestroy', playerId, selfInstanceId, candidateInstanceIds, reason,
      }],
    },
  };
}

// A third shape, distinct from both of the above: "discard 2 cards OR
// destroy 1 hero" (Purple Worm) - a choice between different EFFECTS, not
// just which card an already-decided effect applies to. `options` is pure
// data (`[{ key, label }, ...]`) rather than embedded functions - state gets
// serialized for remote play, so the actual resolution logic lives
// code-side in actions/handChoiceActions.js's EFFECT_CHOICE_RESOLVERS,
// looked up by (cardId, chosen key) once the player answers. Same
// Battle/Aftermath-timing constraint as queueHandChoice above (can't
// resolve up front - which monster even needs a choice isn't known until
// it's fought), so it goes through the same pending-queue + mandatory
// follow-up dispatch pattern (RESOLVE_EFFECT_CHOICE).
export function queueEffectChoice(state, {
  playerId, cardId, options, reason,
}) {
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [...queue, {
        kind: 'effectChoice', playerId, cardId, options, reason,
      }],
    },
  };
}

// A fourth shape: "Choose 1 hero. If that hero's Attack Value is 5 or
// higher, defeat this card (you gain the XP). Otherwise, destroy that
// hero." (Rot Grub) - still fundamentally "pick 1 card," so it reuses the
// normal RESOLVE_HAND_CHOICE action/UI (no new pick-a-card interaction
// needed, unlike Purple Worm's queueEffectChoice above) - only the
// RESOLUTION branches conditionally on the chosen card's own computed
// Attack Value (actions/handChoiceActions.js's resolveHandChoice special-
// cases `kind === 'attackValueChoice'`). Genuinely novel: this is the first
// card whose outcome depends on comparing a chosen hero's own stat against
// a threshold, rather than a fixed verb (discard/destroy) applying to
// whichever card gets picked.
export function queueAttackValueChoice(state, {
  playerId, monsterInstanceId, threshold, candidateInstanceIds, reason,
}) {
  if (!candidateInstanceIds.length) return state;
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [...queue, {
        kind: 'attackValueChoice', playerId, monsterInstanceId, threshold, candidateInstanceIds, reason,
      }],
    },
  };
}

// A fifth shape: "destroy 1 of the revealed [weapons/items]" (Gnoll) -
// still fundamentally "pick 1 card" (reuses RESOLVE_HAND_CHOICE/
// resolveHandChoice, kind === 'discardPileChoice'), but the candidates sit
// in the player's DISCARD pile, not their hand - every other pick-a-card
// choice in this engine assumes hand membership (queueHandChoice's own
// candidates, and resolveHandChoice's validation). Resolved via a small
// discard-pile counterpart to destroyFromHand rather than reusing it
// directly (destroyFromHand only ever touches `hand`).
export function queueDiscardPileChoice(state, { playerId, candidateInstanceIds, reason }) {
  if (!candidateInstanceIds.length) return state;
  const queue = state.meta.pendingHandChoices || [];
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingHandChoices: [...queue, {
        kind: 'discardPileChoice', playerId, candidateInstanceIds, reason,
      }],
    },
  };
}

// Counterpart to destroyFromHand for the rare "destroy a card sitting in
// the discard pile" shape (see queueDiscardPileChoice above) - curse-aware
// the same universal way destroyFromHand is (Matt: "diseases and curses are
// interchangeable... two words for the same thing").
export function destroyFromDiscard(state, playerId, instanceId) {
  const def = getInstanceDefinition(state, instanceId);
  const withoutCard = updatePlayer(state, playerId, (p) => ({
    ...p, discard: removeFromArray(p.discard, instanceId), stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
  }));
  if (!def.traits?.includes('Curse')) return withoutCard;
  return { ...withoutCard, village: { ...withoutCard.village, curseDeck: [...withoutCard.village.curseDeck, instanceId] } };
}

// Root of Corruption, War Incarnate: "Trophy: Ignore 1 hero with the
// highest Strength." Rulebook FAQ ("What does 'Ignore' mean?"): "the
// [something] cannot contribute to the combat in any way, providing no
// Attack, Light, Dungeon abilities, or any other benefit. However, the
// [something] is still present and can be affected by Battle and
// Aftermath effects. An ignored [something] can contribute Spoils
// abilities, since those occur after the monster has been defeated." A
// new primitive distinct from destroy/discard - the card stays in hand,
// unaffected, except its own contribution is voided. Tracked in
// turnFlags.ignoredInstanceIds (resets every turn like any other
// turnFlag) rather than a permanent instance override, since "ignore"
// only ever lasts for the turn it's granted. combat/attackMath.js's
// contributesTraits checks it (voids Attack/Light);
// actions/abilityActions.js's useHandCardAbility checks it too (voids
// Village/Dungeon abilities, but NOT Spoils - the rulebook's own
// exception).
export function markInstanceIgnored(state, playerId, instanceId) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, ignoredInstanceIds: [...p.turnFlags.ignoredInstanceIds, instanceId] },
  }));
}

// "Do What You Can": a mandatory forced effect on N targets is satisfied by
// acting on as many as are actually available (rulebook, general resolution
// rule) - this does not apply to a player's own optional ability usage, which
// requires its full cost/requirement to be met.
export function applyDoWhatYouCan(count, availableTargets) {
  return availableTargets.slice(0, count);
}

// Merges `overrides` onto a specific card instance (see cardLookup.js) - used
// for the handful of effects that permanently change one physical copy's
// stats for the rest of the game (Belac gaining Health, etc.), as opposed to
// a per-turn bonus (which belongs in turnFlags instead).
export function applyInstanceOverride(state, instanceId, overrides) {
  return {
    ...state,
    instances: {
      ...state.instances,
      [instanceId]: { ...state.instances[instanceId], ...overrides },
    },
  };
}

// Every printed "Add/suffers Strength +-N [to/on] a hero" ability
// (Thunderstone Shard, Dwarven Ale, Bandias Wisdom, Ambrosia, Feast,
// Judgement's "Each hero suffers Strength -2", and others) is a Village/
// Dungeon/Trophy/Battle-timed bonus or penalty with no "permanently"
// wording - it should only last for the turn, unlike applyInstanceOverride's
// own truly-permanent use cases (Belac gaining Health). Still writes
// through applyInstanceOverride (Strength is read off the merged instance
// definition in many places, e.g. dungeonActions.js's weapon-equip weight
// check - not worth threading a live per-turn adjustment through every one
// of those reads), but also tracks the instance in
// turnFlags.temporaryStrengthBoostInstanceIds so actions/turnActions.js's
// endTurn can strip the override back off. Safe to call more than once per
// instance per turn, in either direction (a second card stacking a bonus,
// or a penalty on top of an earlier bonus) - the reset just removes the
// `strength` key entirely, regardless of how many times it changed this
// turn. `amount` can be negative (a penalty); floored at 0 either way,
// matching every printed "suffers Strength -N" card's own floor.
export function grantTemporaryStrength(state, playerId, instanceId, amount) {
  const currentStrength = getInstanceDefinition(state, instanceId).strength || 0;
  const overridden = applyInstanceOverride(state, instanceId, { strength: Math.max(0, currentStrength + amount) });
  return updatePlayer(overridden, playerId, (p) => (
    p.turnFlags.temporaryStrengthBoostInstanceIds.includes(instanceId)
      ? p
      : { ...p, turnFlags: { ...p.turnFlags, temporaryStrengthBoostInstanceIds: [...p.turnFlags.temporaryStrengthBoostInstanceIds, instanceId] } }
  ));
}

// Into the Abyss, Xenomorph (all 4 levels): "This card becomes a duplicate
// of an equal or lower level non-Xenomorph hero present..." A FULL identity
// replace, not a stat override like applyInstanceOverride above -
// cardLookup.js's getInstanceDefinition resolves instance.definitionId
// through its byId map, and every other place in the engine that reads a
// card's identity (leveling's own group lookup, scoring, the effect
// registry's own per-definitionId handler lookups) already goes through
// that same function, so overwriting definitionId here is transparently
// correct everywhere else with no special-casing needed. Drops any stale
// override keys the OLD identity might have accumulated (a full replace,
// not a merge) rather than risk them bleeding into the new one.
//
// Matt reported this directly: "the Xeno[morph] duplicated a Regian, and
// it was unable to use its newly acquired dungeon ability because the
// Regian it duplicated already had used its dungeon ability, but the
// newly created card should still be able to use its dungeon ability so
// we don't want to carry over the status of the card that gets
// duplicated." Root cause (see abilityActions.js's useHandCardAbility for
// the actual fix): Xenomorph's OWN "become a duplicate" ability is itself
// registered at dungeon:0, the exact same timing+index most single-line
// dungeon heroes (Regian included) use for their own ability -
// usedAbilityInstanceIds has no way to tell "Xenomorph's dungeon:0 was
// used" apart from "[new identity]'s dungeon:0 was used" once both share
// the same instanceId:timing:abilityIndex key. Nothing to do here beyond
// the plain identity swap.
export function becomeDuplicateOf(state, instanceId, targetDefinitionId) {
  return { ...state, instances: { ...state.instances, [instanceId]: { definitionId: targetDefinitionId } } };
}

// Magma Wyrm: "this card switches ranks with the monster in front of it" -
// a genuine swap (the only "monster relocates within the hall" wording this
// engine had a precedent for until Sathosh/Rockslide below).
export function moveHallCard(state, fromRank, toRank) {
  const dungeonHall = [...state.dungeonHall];
  [dungeonHall[fromRank], dungeonHall[toRank]] = [dungeonHall[toRank], dungeonHall[fromRank]];
  return { ...state, dungeonHall };
}

// Numenera, Sathosh: "After you select this monster to fight, move it to
// rank 3." / Root of Corruption, Rockslide: "Move this monster to the
// highest rank (once per turn)." Unlike Magma Wyrm's own explicit "switches
// ranks with" (moveHallCard above), these just say "move" - Matt,
// confirming directly: this is a true relocate, not a swap - everyone
// between the old and new rank shifts by one to make room, rather than
// trading places with whoever's currently at the destination. A plain
// remove-then-reinsert (Array.prototype.splice) does exactly this for
// either direction: removing from `fromRank` first closes the gap left
// behind (shifting everything after it back by one), then inserting at
// `toRank` in that already-closed array pushes everything at/after it
// forward by one - no separate "which direction" branching needed.
export function shiftHallCardToRank(state, fromRank, toRank) {
  if (fromRank === toRank) return state;
  const dungeonHall = [...state.dungeonHall];
  const [moving] = dungeonHall.splice(fromRank, 1);
  dungeonHall.splice(toRank, 0, moving);
  return { ...state, dungeonHall };
}

// Root of Corruption, Circle of Protection: "On your turn, place this card
// in front of you. It remains in play." The first card needing an "in
// front of you" zone distinct from hand/deck/discard - player.
// inPlayInstanceIds (state/createGameState.js) is a plain array that simply
// isn't touched by the routine end-of-turn hand-clear or by turnFlags reset
// - "remains in play" until whatever consumes it says otherwise, no special
// persistence logic needed beyond "nothing clears it automatically." Matt:
// "I think the 'in front of you' zone is a concept we could use for the
// familiar as well... maybe even use this for cards that get put in front
// of other players by monster effects or other things." - the familiar
// (familiarActions.js's takeFamiliar/useFamiliarAbility, turnActions.js's
// endTurn) and Mind Control's assigned monster (effectRegistry/villages.js,
// state/selectors.js's getAssignedMonsterInstanceId) both now share this
// same array too, each distinguishable from Circle of Protection (and from
// each other) purely by their own card identity/type - no "kind" tag
// needed on the array itself.
export function placeInFrontOfPlayer(state, playerId, instanceId) {
  return updatePlayer(state, playerId, (p) => ({
    ...p,
    hand: removeFromArray(p.hand, instanceId),
    inPlayInstanceIds: [...p.inPlayInstanceIds, instanceId],
  }));
}

// Circle of Protection's own text: "Cancel the next Raid, Breach, or
// Aftermath effect that takes place on your turn and destroy this card."
// Checked at the top of every Raid/Breach/Aftermath dispatch site
// (dungeon/hall.js's compactAndRefill, combat/resolveCombat.js's own
// aftermath handling AND resolveHandCardAftermaths), right before a handler
// is actually about to fire - the first call site to find an eligible copy
// consumes (destroys) it, which naturally limits this to exactly one
// cancellation per copy in play (rulebook FAQ: "If you have more than one
// copy of this card in front of you, you only need to destroy one for each
// cancellation" - multiple copies stack additively, one destroyed per
// cancellation). Every call site checks for a REGISTERED HANDLER first and
// only calls this once it knows a real effect is actually about to
// resolve - an unimplemented Raid/Breach/Aftermath line would have been a
// no-op either way, so it shouldn't burn a copy of this card for nothing.
export function consumeCircleOfProtection(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const instanceId = player?.inPlayInstanceIds.find((id) => getInstanceDefinition(state, id).id === 'village-circle-of-protection');
  if (!instanceId) return { state, cancelled: false };
  const nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    inPlayInstanceIds: removeFromArray(p.inPlayInstanceIds, instanceId),
    stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
  }));
  return { state: nextState, cancelled: true };
}

// Pulls a monster straight out of the hall into `playerId`'s discard pile,
// leaving its rank empty (NOT compacted/refilled - callers decide when that
// happens: handChoiceActions.js's defeatMonsterOutright refills immediately,
// Greatsword (effectRegistry/villages.js) deliberately doesn't, deferring to
// the natural end-of-turn refill in actions/turnActions.js's endTurn). A
// no-op if the monster already left the hall some other way in the meantime.
export function removeMonsterFromHallToDiscard(state, playerId, monsterInstanceId) {
  const hallRankIndex = state.dungeonHall.indexOf(monsterInstanceId);
  if (hallRankIndex === -1) return state;
  const dungeonHall = [...state.dungeonHall];
  dungeonHall[hallRankIndex] = null;
  return updatePlayer({ ...state, dungeonHall }, playerId, (p) => ({ ...p, discard: [...p.discard, monsterInstanceId] }));
}

// Into the Abyss, Grim (all 4 levels): "Spoils: Set the defeated monster
// [and this card / and any monsters present] aside. Return [it/them] to
// your deck at the end of the game." Removes from wherever the target
// currently sits (hand - Grim's OWN card, still in hand when Spoils fires -
// or discard - the just-defeated monster, which lands there automatically
// before Spoils resolves) and appends to the new setAsideInstanceIds zone
// (createGameState.js), permanently untouchable by any normal hand/deck/
// discard mechanic - never drawn, never shuffled, never interacted with
// again until the game is already over. See state/selectors.js's
// getPlayerCollectionInstanceIds for how this still scores.
export function setAsideForGame(state, playerId, instanceId) {
  return updatePlayer(state, playerId, (p) => ({
    ...p,
    hand: removeFromArray(p.hand, instanceId),
    discard: removeFromArray(p.discard, instanceId),
    equippedWeapons: unequipIfEquippedWeapon(p, instanceId),
    setAsideInstanceIds: [...p.setAsideInstanceIds, instanceId],
  }));
}
